import { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { formatDateSafe } from '../../../../utilities/date';

const toNumber = (value) => Number.parseFloat(value || 0);

const getRentaTotal = (loan) =>
  toNumber(loan.interes_total_por_prestamo ?? loan.renta_total);

const ReportePrestamoDashboardExcel = ({
  loans = [],
  filters = {},
  meta = {},
  onComplete,
  onExcelGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const hasGeneratedRef = useRef(false);

  const generateExcelReport = useCallback(() => {
    if (isGenerating || hasGeneratedRef.current) return;

    hasGeneratedRef.current = true;
    setIsGenerating(true);

    try {
      if (loans.length === 0) {
        toast.warning('No hay préstamos para generar el Excel.');
        return;
      }

      const reportData = loans.map((loan, index) => ({
        'N°': index + 1,
        'ID Préstamo': loan.id || '-',
        Cliente: loan.cliente || '-',
        Asesor: loan.asesor || loan.asesor_id || '-',
        'Fecha Desembolso': formatDateSafe(loan.fecha_generacion),
        Monto: toNumber(loan.monto),
        'Interés (%)': toNumber(loan.interes_porcentaje),
        Total: toNumber(loan.total),
        Frecuencia: loan.frecuencia || '-',
        'Progreso Cuotas': loan.progreso_cuotas || '0 de 0',
        'Capital Pendiente': toNumber(loan.capital_pendiente),
        'Saldo Pendiente': toNumber(loan.saldo_pendiente),
        'Renta Total': getRentaTotal(loan),
        'Renta Pendiente': toNumber(loan.renta_pendiente),
        Modalidad: loan.modalidad || '-',
        Estado: loan.estado || '-',
        'Abonado Por': loan.abonado_por || '-',
        'Registrado Por': loan.registrado_por || '-',
      }));

      reportData.push({
        'N°': '',
        'ID Préstamo': '',
        Cliente: 'TOTALES',
        Asesor: '',
        'Fecha Desembolso': '',
        Monto: toNumber(meta.total_monto),
        'Interés (%)': '',
        Total: toNumber(meta.total_total),
        Frecuencia: '',
        'Progreso Cuotas': '',
        'Capital Pendiente': toNumber(meta.total_capital_pendiente),
        'Saldo Pendiente': toNumber(meta.total_saldo_pendiente),
        'Renta Total': toNumber(meta.total_intereses_globales),
        'Renta Pendiente': toNumber(meta.total_renta_pendiente),
        Modalidad: '',
        Estado: '',
        'Abonado Por': '',
        'Registrado Por': '',
      });

      const resumenData = [
        ['REPORTE DE PRÉSTAMOS'],
        [''],
        ['FILTROS APLICADOS'],
        ['Fecha inicio:', filters.date_start ? formatDateSafe(filters.date_start) : 'TODOS'],
        ['Fecha fin:', filters.date_end ? formatDateSafe(filters.date_end) : 'TODOS'],
        ['Asesor:', filters.advisor || 'TODOS'],
        ['Estado:', filters.estado || 'TODOS'],
        ['Modalidad:', filters.modalidad || 'TODOS'],
        ['Abonado por:', filters.abonado_por || 'TODOS'],
        [''],
        ['RESUMEN'],
        ['N. préstamos:', meta.total ?? loans.length],
        ['Desembolso total:', toNumber(meta.total_monto)],
        ['Total a pagar:', toNumber(meta.total_total)],
        ['Capital pendiente:', toNumber(meta.total_capital_pendiente)],
        ['Saldo pendiente:', toNumber(meta.total_saldo_pendiente)],
        ['Renta total:', toNumber(meta.total_intereses_globales)],
        ['Renta pendiente:', toNumber(meta.total_renta_pendiente)],
        ['Renta ejecutada:', toNumber(meta.renta_ejecutada)],
        ['Clientes nuevos:', meta.clientes_nuevos_con_prestamo ?? 0],
        ['Fecha de generación:', formatDateSafe(new Date().toISOString())],
      ];

      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.aoa_to_sheet(resumenData);
      summarySheet['!cols'] = [{ wch: 28 }, { wch: 24 }];

      ['B13', 'B14', 'B15', 'B16', 'B17', 'B18', 'B19'].forEach((cellRef) => {
        if (summarySheet[cellRef] && typeof summarySheet[cellRef].v === 'number') {
          summarySheet[cellRef].z = '#,##0.00';
          summarySheet[cellRef].t = 'n';
        }
      });

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

      const detailSheet = XLSX.utils.json_to_sheet(reportData);
      detailSheet['!cols'] = [
        { wch: 5 },
        { wch: 12 },
        { wch: 34 },
        { wch: 28 },
        { wch: 16 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 12 },
        { wch: 12 },
        { wch: 18 },
        { wch: 28 },
      ];

      const totalRowNum = reportData.length + 1;
      const currencyColumns = ['F', 'H', 'K', 'L', 'M', 'N'];

      for (let row = 2; row <= totalRowNum; row += 1) {
        currencyColumns.forEach((column) => {
          const cellRef = `${column}${row}`;
          if (detailSheet[cellRef] && typeof detailSheet[cellRef].v === 'number') {
            detailSheet[cellRef].z = '#,##0.00';
            detailSheet[cellRef].t = 'n';
          }
        });

        const interestCellRef = `G${row}`;
        if (detailSheet[interestCellRef] && typeof detailSheet[interestCellRef].v === 'number') {
          detailSheet[interestCellRef].z = '0.00';
          detailSheet[interestCellRef].t = 'n';
        }
      }

      XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detalle');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);

      if (onExcelGenerated) {
        onExcelGenerated(url);
      }

      toast.success(`Excel generado correctamente. ${loans.length} préstamos exportados.`);
    } catch (error) {
      console.error('Error generando Excel de préstamos:', error);
      toast.error(`Error al generar el Excel: ${error.message}`);
    } finally {
      setIsGenerating(false);
      if (onComplete) {
        onComplete();
      }
    }
  }, [filters, isGenerating, loans, meta, onComplete, onExcelGenerated]);

  useEffect(() => {
    generateExcelReport();
  }, [generateExcelReport]);

  return (
    <div className="hidden">
      {isGenerating && (
        <div className="fixed top-4 right-4 z-50 rounded-lg bg-green-600 p-3 text-white shadow-lg">
          <div className="flex items-center">
            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
            Generando reporte Excel...
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportePrestamoDashboardExcel;
